import { useEffect, useRef, useState } from "react";
import { ChatMessage } from "@/entities/messaging/types";
import { DreamerState } from "@/components/controls/DreamerButton/DreamerButton";

const processedNotificationKeysByChannel = new Map<string, Set<string>>();
const MAX_PROCESSED_NOTIFICATION_KEYS = 500;

interface UseNotificationManagerOptions {
  notifications: ChatMessage[];
  externalState: DreamerState;
  channelName?: string;
}

interface SyncMessage {
  count: number;
  state: DreamerState;
}

interface UseNotificationManagerReturn {
  activeNotifications: ChatMessage[];
  notificationCount: number;
  currentState: DreamerState;
  syncData: SyncMessage;
}

export const useNotificationManager = ({
  notifications = [],
  externalState,
  channelName = "notifications.dreamerbutton.dreamhub"
}: UseNotificationManagerOptions): UseNotificationManagerReturn => {
  const storageKey = `notification-count-${channelName}`;

  const getStoredCount = (): number => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? parseInt(stored, 10) : 0;
    } catch {
      return 0;
    }
  };

  const [activeNotifications, setActiveNotifications] = useState<ChatMessage[]>(
    []
  );
  const [notificationCount, setNotificationCount] = useState(getStoredCount);
  const [currentState, setCurrentState] = useState(externalState);
  const queuedNotifications = useRef<ChatMessage[]>([]);
  const isVisible = useRef(true);
  const channel = useRef<BroadcastChannel | null>(null);

  const getNotificationKey = (notification: ChatMessage): string => {
    if (notification.id) {
      return `id:${notification.id}`;
    }

    return `msg:${notification.message}|ts:${notification.timestamp}`;
  };

  const getProcessedKeys = (): Set<string> => {
    const existingKeys = processedNotificationKeysByChannel.get(channelName);
    if (existingKeys) {
      return existingKeys;
    }

    const newKeys = new Set<string>();
    processedNotificationKeysByChannel.set(channelName, newKeys);
    return newKeys;
  };

  const markNotificationsProcessed = (incomingNotifications: ChatMessage[]) => {
    if (incomingNotifications.length === 0) return;

    const processedKeys = getProcessedKeys();

    incomingNotifications.forEach((notification) => {
      processedKeys.add(getNotificationKey(notification));
    });

    if (processedKeys.size > MAX_PROCESSED_NOTIFICATION_KEYS) {
      const trimmedKeys = Array.from(processedKeys).slice(
        -MAX_PROCESSED_NOTIFICATION_KEYS
      );
      processedNotificationKeysByChannel.set(channelName, new Set(trimmedKeys));
    }
  };

  // Create sync data object
  const syncData: SyncMessage = {
    count: notificationCount,
    state: currentState
  };

  const persistCount = (count: number) => {
    try {
      if (count > 0) {
        localStorage.setItem(storageKey, count.toString());
      } else {
        localStorage.removeItem(storageKey);
      }
    } catch {
      // Silently ignore localStorage errors (e.g., private browsing mode)
    }
  };

  // Setup sync between tabs
  useEffect(() => {
    channel.current = new BroadcastChannel(channelName);

    const handleMessage = (e: MessageEvent<SyncMessage>) => {
      const { data: eventData } = e;
      setNotificationCount(eventData.count);
      setCurrentState(eventData.state);
      persistCount(eventData.count);
    };

    channel.current.addEventListener("message", handleMessage);

    return () => {
      channel.current?.removeEventListener("message", handleMessage);
      channel.current?.close();
      channel.current = null;
    };
  }, [channelName, storageKey]);

  // Handle new notifications and counting
  useEffect(() => {
    if (!notifications.length) return;

    const processedKeys = getProcessedKeys();
    const newNotifications = notifications.filter(
      (notification) => !processedKeys.has(getNotificationKey(notification))
    );

    if (newNotifications.length === 0) return;

    markNotificationsProcessed(newNotifications);

    if (
      externalState !== DreamerState.Normal &&
      externalState !== DreamerState.Collapsed
    ) {
      setNotificationCount((prev) => {
        const newCount = prev + newNotifications.length;
        setCurrentState(externalState);
        persistCount(newCount);
        channel.current?.postMessage({
          count: newCount,
          state: externalState
        });
        return newCount;
      });
    }

    if (isVisible.current) {
      setActiveNotifications(newNotifications);
    } else {
      queuedNotifications.current = [
        ...queuedNotifications.current,
        ...newNotifications
      ];
    }
  }, [notifications, externalState]);

  // Reset on state changes
  useEffect(() => {
    if (
      externalState === DreamerState.Normal ||
      externalState === DreamerState.Collapsed
    ) {
      setNotificationCount(0);
      setCurrentState(externalState);
      queuedNotifications.current = [];
      processedNotificationKeysByChannel.delete(channelName);
      persistCount(0); // Clear localStorage
      channel.current?.postMessage({ count: 0, state: externalState });
    }
  }, [channelName, externalState]);

  // Handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisible.current = !document.hidden;

      if (!document.hidden && queuedNotifications.current.length > 0) {
        const latestNotifications = queuedNotifications.current;
        queuedNotifications.current = [];
        setActiveNotifications(latestNotifications);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return {
    activeNotifications,
    notificationCount,
    currentState,
    syncData
  };
};
